/**
 * Phase 140A-replay-fix — Egress kickoff trigger.
 *
 * Mounts inside <LiveKitRoom>. The moment the broadcaster's first local
 * track is published (the only reliable signal that the LiveKit room
 * actually exists), this component fires
 *
 *     POST /api/livex/streams/{streamId}/start-recording
 *
 * which kicks off Room Composite Egress. Idempotent server-side so the
 * 8-second delayed fallback kickoff scheduled at stream-create is harmless
 * (atomic claim filter skips duplicate launches).
 *
 * Renders nothing — pure side-effect component. Wrapped in a try/catch so
 * a network blip never crashes the broadcaster cockpit.
 */
import { useEffect, useRef } from 'react';
import { useLocalParticipant } from '@livekit/components-react';
import api from '@/lib/api';

export default function EgressKickoff({ streamId }) {
  const { localParticipant } = useLocalParticipant();
  const firedRef = useRef(false);

  useEffect(() => {
    if (!streamId || !localParticipant || firedRef.current) return;
    // The participant may already have published before this effect runs
    // (fast reconnect, hot reload, etc.) — guard against double-fire AND
    // against firing before the first track lands.
    const pubs = Array.from(localParticipant.trackPublications?.values?.() || []);
    if (pubs.length === 0) return;
    firedRef.current = true;
    (async () => {
      try {
        await api.post(`/livex/streams/${streamId}/start-recording`);
      } catch (_e) {
        // Best-effort; the 8-second fallback kickoff still runs server-side.
      }
    })();
  }, [streamId, localParticipant, localParticipant?.trackPublications?.size]);

  return null;
}
